/*jshint esversion: 11 */

// Player da área do aluno (cursos). HLS criptografado entregue por rotas autenticadas.
// Marca d'água, bloqueio de menu de contexto e sessão única são dissuasão, não DRM.

const HEARTBEAT_MS = 15000;
const WATERMARK_MOVE_MS = 20000;
const MAX_RECOVERIES = 3;

function mountWatermark(container, text) {
    const mark = document.createElement('div');
    mark.textContent = text;
    mark.setAttribute('aria-hidden', 'true');
    mark.dataset.courseWatermark = '1';

    let expected = '';
    const place = () => {
        // Posição em px com as medidas reais: a marca nunca passa da borda (nem em mobile).
        const maxLeft = Math.max(0, container.clientWidth - mark.offsetWidth);
        const maxTop = Math.max(0, container.clientHeight - mark.offsetHeight);
        const left = Math.min(maxLeft, maxLeft * (0.05 + Math.random() * 0.8));
        const top = Math.min(maxTop, maxTop * (0.08 + Math.random() * 0.75));
        expected = 'position:absolute;z-index:30;pointer-events:none;user-select:none;'
            + 'color:rgba(255,255,255,.45);font:600 14px/1.2 sans-serif;white-space:nowrap;'
            + 'text-shadow:0 0 3px rgba(0,0,0,.8);display:block;opacity:1;visibility:visible;'
            + `left:${Math.round(left)}px;top:${Math.round(top)}px;`;
        mark.setAttribute('style', expected);
    };

    // Entra no DOM antes de medir (offsetWidth).
    mark.setAttribute('style', 'position:absolute;visibility:hidden;white-space:nowrap;font:600 14px/1.2 sans-serif;');
    container.appendChild(mark);
    place();

    // Reinsere/restaura se o overlay for removido ou escondido pelo DevTools.
    const observer = new MutationObserver(() => {
        if (!container.contains(mark)) {
            container.appendChild(mark);
        }
        if (mark.getAttribute('style') !== expected) {
            mark.setAttribute('style', expected);
        }
    });
    observer.observe(container, {childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden']});

    const timer = setInterval(place, WATERMARK_MOVE_MS);
    document.addEventListener('fullscreenchange', place);

    return {
        destroy() {
            clearInterval(timer);
            observer.disconnect();
            document.removeEventListener('fullscreenchange', place);
        },
    };
}

document.addEventListener('alpine:init', () => {
    window.Alpine.data('coursePlayer', (cfg) => ({
        percent: cfg.percent ?? 0,
        message: null,
        notice: null,
        canRetry: false,
        stopped: false,
        completed: false,
        watched: 0,
        lastTime: null,
        hls: null,
        timers: [],
        watermark: null,
        destroyed: false,
        recoveries: 0,
        onVisibility: null,

        async init() {
            const video = this.$refs.video;

            if (!video) {
                return;
            }

            video.addEventListener('contextmenu', (e) => e.preventDefault());
            this.watermark = mountWatermark(this.$refs.box, cfg.watermark);

            video.addEventListener('loadedmetadata', () => {
                if (cfg.startAt > 0 && video.duration && cfg.startAt < video.duration - 10) {
                    video.currentTime = cfg.startAt;
                }
            }, {once: true});
            video.addEventListener('timeupdate', () => this.track(video));
            video.addEventListener('playing', () => { this.recoveries = 0; });
            video.addEventListener('pause', () => this.beat(video));
            video.addEventListener('ended', () => this.beat(video, true));
            video.addEventListener('error', () => this.fail());
            this.timers.push(setInterval(() => !video.paused && this.beat(video), HEARTBEAT_MS));
            this.onVisibility = () => document.hidden && this.beacon(video);
            document.addEventListener('visibilitychange', this.onVisibility);

            await this.attach();
        },

        // (Re)inicia a fonte do vídeo: carga inicial e botão "Tentar de novo".
        async attach() {
            const video = this.$refs.video;

            if (this.hls) {
                this.hls.destroy();
                this.hls = null;
            }

            if (video.canPlayType('application/vnd.apple.mpegurl')) {
                video.src = cfg.src; // Safari/iOS: HLS nativo
                video.load();
                return;
            }

            const {default: Hls} = await import('hls.js');

            if (this.destroyed) {
                return;
            }

            if (!Hls.isSupported()) {
                this.message = cfg.messages.unsupported;
                return;
            }

            this.hls = new Hls({xhrSetup: (xhr, url) => { xhr.withCredentials = new URL(url, location.href).origin === location.origin; }});
            this.hls.loadSource(cfg.src);
            this.hls.attachMedia(video);
            this.hls.on(Hls.Events.ERROR, (_e, data) => {
                if (!data.fatal) {
                    return;
                }
                // Recuperação automática (até 3 vezes) antes de investigar a causa.
                if (this.recoveries < MAX_RECOVERIES && data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                    this.recoveries++;
                    this.hls.startLoad();
                } else if (this.recoveries < MAX_RECOVERIES && data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                    this.recoveries++;
                    this.hls.recoverMediaError();
                } else {
                    this.fail();
                }
            });
        },

        fail() {
            this.canRetry = true;
            this.diagnose();
        },

        retry() {
            if (this.stopped) {
                return;
            }
            this.recoveries = 0;
            this.message = null;
            this.canRetry = false;
            this.attach();
        },

        track(video) {
            const t = video.currentTime;
            if (this.lastTime !== null) {
                const delta = t - this.lastTime;
                if (delta > 0 && delta < 2) {
                    this.watched += delta;
                }
            }
            this.lastTime = t;
        },

        payload(video, completed) {
            const watched = Math.min(120, Math.round(this.watched));
            this.watched = 0;
            return {
                lesson_id: cfg.lessonId,
                position: Math.floor(video.currentTime || 0),
                watched,
                completed: completed ? 1 : 0,
            };
        },

        async beat(video, completed = false) {
            if (this.stopped) {
                return;
            }
            await this.send(this.payload(video, completed));
        },

        async send(body) {
            try {
                const res = await fetch(cfg.progressUrl, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': cfg.csrf,
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify(body),
                });

                if (res.ok) {
                    const data = await res.json();
                    this.percent = data.percent ?? this.percent;
                    this.notice = null;
                    return true;
                }

                if (res.status === 403) {
                    this.block((await res.json().catch(() => ({}))).code);
                } else if (res.status === 401 || res.status === 419) {
                    this.notice = cfg.messages.session_expired;
                } else if (res.status === 429) {
                    this.notice = cfg.messages.rate_limited;
                }
            } catch (e) {
                // Falha de rede no heartbeat: tenta de novo no próximo ciclo.
            }

            return false;
        },

        beacon(video) {
            if (this.stopped || !navigator.sendBeacon) {
                return;
            }
            const body = this.payload(video, false);
            const form = new FormData();
            Object.entries(body).forEach(([k, v]) => form.append(k, v));
            form.append('_token', cfg.csrf);
            navigator.sendBeacon(cfg.progressUrl, form);
        },

        // Descobre o motivo de uma falha de carga (sessão substituída, acesso encerrado) lendo o JSON da playlist.
        async diagnose() {
            if (this.stopped) {
                return;
            }
            try {
                const res = await fetch(cfg.src, {credentials: 'same-origin', headers: {'Accept': 'application/json'}});
                if (res.status === 403) {
                    this.block((await res.json().catch(() => ({}))).code);
                    return;
                }
                if (res.status === 429) {
                    this.message = cfg.messages.rate_limited;
                    return;
                }
            } catch (e) {
                // sem rede
            }
            this.message = cfg.messages.error;
        },

        block(code) {
            this.stopped = true;
            this.canRetry = false;
            this.message = code === 'session_replaced' ? cfg.messages.session_replaced : cfg.messages.access_denied;
            const video = this.$refs.video;
            if (video) {
                video.pause();
            }
            if (this.hls) {
                this.hls.destroy();
            }
        },

        fullscreen() {
            const box = this.$refs.box;
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else if (box.requestFullscreen) {
                box.requestFullscreen();
            }
        },

        async markTextDone() {
            // Só mostra "concluída" se o servidor confirmou (sessão expirada/403 não pode parecer sucesso).
            this.completed = await this.send({lesson_id: cfg.lessonId, completed: 1});
            if (!this.completed && !this.notice && !this.stopped) {
                this.notice = cfg.messages.save_failed;
            }
        },

        destroy() {
            this.destroyed = true;
            this.timers.forEach(clearInterval);
            this.watermark?.destroy();
            if (this.onVisibility) {
                document.removeEventListener('visibilitychange', this.onVisibility);
            }
            if (this.hls) {
                this.hls.destroy();
            }
        },
    }));
});
